import React, { useState } from 'react';
import { Pause, Play } from 'lucide-react';
import { useHeadlines, timeAgo } from '@/hooks/useSystemStatus';
import { severityMeta } from '@/lib/severity';

/**
 * Scrolling headline strip fed by real data: the signed-in customer's
 * latest matched stories, or public OSINT headlines for visitors. Hidden
 * when there is nothing to show.
 *
 * Motion (WCAG 2.2.2): pauses on hover and keyboard focus, has a
 * pause/play button (choice remembered per browser), and stops entirely
 * under prefers-reduced-motion (theme.css). The second copy of the items
 * that makes the loop seamless is aria-hidden, so screen readers get one
 * plain list.
 */
const SEV_CLASS = { critical: 'crit', high: 'high', medium: 'med', low: 'low', info: 'info' };
const PAUSE_KEY = 'ticker_paused';

const readPaused = () => {
  try { return localStorage.getItem(PAUSE_KEY) === '1'; } catch { return false; }
};

function TickerItems({ items, hidden }) {
  return (
    <>
      {items.map((it, i) => {
        const sev = severityMeta(it.severity);
        return (
          <span key={`${it.id || it.attack_id || i}`} className="is-ticker-item" aria-hidden={hidden || undefined} role={hidden ? undefined : 'listitem'}>
            <span className={`tk-sev tk-sev--${SEV_CLASS[sev.key]}`}>{sev.short}</span>
            {it.threat_actor && <strong>{it.threat_actor}</strong>}
            <span>{it.name}</span>
            {(it.source_count || 1) > 1 && <span className="tk-meta">{it.source_count} sources</span>}
            <span className="tk-meta" aria-hidden="true">·</span>
            <span className="tk-meta">{timeAgo(it.discovered_at)}</span>
          </span>
        );
      })}
    </>
  );
}

export default function IntelTicker({ label = 'Latest' }) {
  const { data } = useHeadlines();
  const [paused, setPaused] = useState(readPaused);
  const items = (Array.isArray(data) ? data : []).filter((x) => x && x.name).slice(0, 10);
  if (!items.length) return null;

  const toggle = () => {
    setPaused((p) => {
      try { localStorage.setItem(PAUSE_KEY, p ? '0' : '1'); } catch { /* storage blocked */ }
      return !p;
    });
  };

  return (
    <div
      className="is-ticker"
      data-testid="intel-ticker"
      data-paused={paused ? 'true' : 'false'}
      role="region"
      aria-label="Latest intelligence headlines"
    >
      <div className="is-ticker-tag">{label}</div>
      <div className="is-ticker-viewport">
        <div className="is-ticker-track">
          <span role="list" style={{ display: 'contents' }}>
            <TickerItems items={items} />
          </span>
          <TickerItems items={items} hidden />
        </div>
      </div>
      <button
        type="button"
        className="is-ticker-toggle"
        onClick={toggle}
        aria-pressed={paused}
        aria-label="Pause scrolling headlines"
        title="Pause scrolling headlines"
        data-testid="ticker-toggle"
      >
        {paused ? <Play size={13} aria-hidden="true" /> : <Pause size={13} aria-hidden="true" />}
      </button>
    </div>
  );
}
